import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, Sparkles } from 'lucide-react';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';

const FAQS = [
  {
    q: 'What does Poettik actually do?',
    a: 'Poettik turns a product description into a ready-to-film TikTok pack — text hooks, visual hooks, verbal hooks and a 15-60 second script tailored for TikTok Shop creators. It runs on best-in-class AI, tuned with prompts designed for UK and US affiliate marketing.',
  },
  {
    q: 'How long is the free trial?',
    a: '3 days, no credit card required. You\'ll get up to 5 generations a day during the trial so you can put Poettik through its paces on real products before committing.',
  },
  {
    q: 'Which plan is right for me?',
    a: 'Basic (£9.99/mo) gives you 5 generations per day — perfect if you\'re posting once or twice a day. Pro (£15.99/mo) gives you 15 generations a day plus respin options on scripts and hooks, ideal for high-volume affiliates or agencies running multiple accounts.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancellation is one click in your Subscription page; you\'ll keep access until the end of your current billing period and you won\'t be charged again.',
  },
  {
    q: 'Will Poettik mention my kids/pets in the script?',
    a: 'Only if it\'s genuinely relevant to the product. The personalisation context you set on your Profile page (gender, age range, kids, pets) is used to tailor tone and pain points, not to shoehorn references in.',
  },
  {
    q: 'What\'s the affiliate programme?',
    a: 'Poettik runs a 3-tier affiliate programme. You earn 30% commission on every direct referral, 7% on referrals from your sub-affiliates, and 3% from their sub-affiliates. Sign up at "Earn with Poettik".',
  },
  {
    q: 'Do you charge in GBP or USD?',
    a: 'GBP by default for UK-based users. Stripe automatically converts to your local card currency at checkout, with the live FX rate.',
  },
  {
    q: 'Why are some scripts about TikTok Shop specifically?',
    a: 'Every prompt is tuned for the TikTok Shop creator commission model — hooks that sell, scripts that fit native TikTok pacing, and visual angles that work on a phone. This isn\'t a generic AI writer.',
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Generated content belongs to you. Your profile data (gender/age/kids/pets) is used only to personalise prompts and is never shared. See our Privacy Policy for full details.',
  },
  {
    q: 'Where do I get support?',
    a: 'The Support page or email hey@poettik.com. We aim to reply within 1 business day.',
  },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = React.useState(idx === 0);
  return (
    <Card variant="glass" className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-5 flex items-center justify-between gap-4 hover:bg-muted/30 transition-colors"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="font-medium text-base sm:text-lg">{q}</span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>
      {open && (
        <CardContent className="pt-0 pb-5 px-5 text-muted-foreground text-sm leading-relaxed">
          {a}
        </CardContent>
      )}
    </Card>
  );
};

const FAQPage = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="faq-page">
      <SEO title="Frequently Asked Questions" description="Answers to common questions about Poettik — pricing, trial, AI content generation, refunds and the affiliate programme." />
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-20 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
      </div>

      <nav className="relative z-50 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <AppNav />
          </div>
        </div>
      </nav>

      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 text-xs text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Frequently asked
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3">
            <span className="gradient-text">Questions</span>, answered
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Everything you need to know about pricing, trials, the affiliate programme and how Poettik tailors content to you.
          </p>
        </div>

        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} idx={i} />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-3">Didn't find what you were after?</p>
          <Link to="/support">
            <Button variant="premium" size="lg" data-testid="faq-support-cta">
              Contact Support
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default FAQPage;
